/** Copyright (c) 2018 T. Rowe Price. All rights reserved. */

import './MobileTransactionTables.scss';
import 'style/components/MobileTable.scss';

import {
  string,
  object,
  number,
  oneOfType,
  arrayOf,
  element
} from 'prop-types';
import React, { Fragment } from 'react';
import cx from 'classnames';
import {
  cloneDeep,
  flow,
  isEmpty,
  isNil,
  isString,
  kebabCase,
  map,
  omit,
  reduce,
  startCase
} from 'lodash';

import CancelTransaction from 'components/layouts/Transaction/CancelTransaction/CancelTransaction';
import {
  Dollar,
  FormattedDate,
  Percent,
  Shares
} from 'components/shared/Text/Text';

import Toggle from 'components/shared/ToggleRP';

import Optioner from 'components/shared/Optioner/Optioner';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';

import {
  isActivePlus,
  isBrokerage,
  isCollegeSavings,
  MUTUAL_FUND
} from 'services/Products';
import {
  ADDRESS_OF_RECORD_DELIVERY_TYPE,
  ELECTRONIC_DELIVERY_TYPE,
  WIRE_DELIVERY_TYPE,
  isBaseTransactionType,
  isExchangeTransactionType,
  isPurchaseTransactionType,
  isRedeemTransactionType,
  isDistributionToSchoolTransactionType
} from 'services/Accounts';
import Content from 'services/Content';

import {
  labelCells,
  sortObjectByKeys,
  valueCells
} from 'components/shared/Table/MobileTableSupport';
import SchoolAddress from './SchoolAddress/SchoolAddress';
import { isMoneyMarketCutoffEnabled } from 'services/FeatureFlags';

const AVAILABLE_SHARES_LABEL = 'All Available Shares';
const REMAINING_SHARES_LABEL = 'All Remaining Shares';
const GO_TUITION = 'Web529Gift';
const DOLLAR_UNIT_TYPE_CODE = 1;
const SHARES_UNIT_TYPE_CODE = 2;
const PERCENT_UNIT_TYPE_CODE = 3;
const AVAILABLE_SHARES_UNIT_TYPE_CODE = 4;
const REMAINING_SHARES_UNIT_TYPE_CODE = 5;

const COL_LABEL_TYPE = 'type';
const COL_LABEL_AMOUNT = 'amount';

const columns = [COL_LABEL_TYPE, COL_LABEL_AMOUNT];

const pendingTableSortOrder = [
  'tradeDate',
  'fromInformation',
  'toInformation',
  'amountInformation',
  'costBasis',
  'userCancellable'
];
const pendingTableComparator = (a, b) =>
  pendingTableSortOrder.indexOf(a) - pendingTableSortOrder.indexOf(b);

const DefaultCell = ({ className }) => <td className={className}>-</td>;

const TableColumns = ({ columns }) => (
  <tr className="columns">
    {columns.map((name) => (
      <th
        scope="col"
        className={cx(
          `mobile-transaction-pending-table-column-${kebabCase(name)}`,
          `e2e-mobile-transaction-pending-table-column-${kebabCase(name)}`,
          'column-header-cell'
        )}
        key={name}>
        {startCase(name)}
      </th>
    ))}
  </tr>
);

TableColumns.propTypes = {
  columns: arrayOf(oneOfType([element, string]))
};

const detailExclusionList = ['bankAccountNumber', 'typeInformation'];

const DetailCollection = ({ details, productType, hasCollegeSavingsOnly }) =>
  flow(
    (d) => omit(d, detailExclusionList),
    (d) => sortObjectByKeys(d, pendingTableComparator),
    (d) =>
      reduce(
        d,
        mapDetailsToRows(details, productType, hasCollegeSavingsOnly),
        []
      )
  )(details);

const mapDetailsToRows =
  (fullDetails, productType, hasCollegeSavingsOnly) => (acc, value, key) =>
    acc.concat(
      DetailRow({
        value,
        name: key,
        fullDetails,
        productType,
        hasCollegeSavingsOnly
      })
    );

const rowLabelClass = (name) =>
  cx(
    'mobile-transaction-pending-table-row-label',
    'e2e-mobile-transaction-pending-table-row-label',
    `${name}-row-label`,
    `e2e-${name}-row-label`
  );

const rowValueClass = (name) =>
  cx(
    'mobile-transaction-pending-table-row-value',
    'e2e-mobile-transaction-pending-table-row-value',
    `${name}-row-value`,
    ` e2e-${name}-row-value`
  );

const DetailRow = ({
  value,
  name,
  fullDetails,
  productType,
  hasCollegeSavingsOnly
}) => {
  const transactionType = fullDetails.fromInformation?.type;
  const classLabel = rowValueClass(name);
  const collegeSavingsLabelCellOverrides = cloneDeep(labelCellOverrides);
  delete collegeSavingsLabelCellOverrides.costBasis;
  const collegeSavingsValueCellOverrides = cloneDeep(
    valueCellOverrides(classLabel, transactionType)
  );
  delete collegeSavingsValueCellOverrides.costBasis;

  const labelMapper = labelCells({
    cellLabelClass: classLabel,
    overrides:
      isCollegeSavings(productType) || hasCollegeSavingsOnly
        ? collegeSavingsLabelCellOverrides
        : labelCellOverrides
  });

  const cellMapper = valueCells({
    cellValueClass: classLabel,
    overrides:
      isCollegeSavings(productType) || hasCollegeSavingsOnly
        ? collegeSavingsValueCellOverrides
        : valueCellOverrides(classLabel, transactionType)
  });

  const rowClassName = `mobile-transaction-pending-table-row e2e-${name}-row`;

  return (
    !isNil(value) &&
    value && (
      <tr key={name} className={rowClassName}>
        <RowLabelCell className={rowLabelClass(name)}>
          {(labelMapper[name] || labelMapper.default)()}
        </RowLabelCell>
        {(cellMapper[name] || cellMapper.default)(value, fullDetails)}
      </tr>
    )
  );
};

DetailRow.propTypes = {
  value: oneOfType([string, number]),
  name: string,
  fullDetails: object
};

const RowLabelCell = ({ children, className }) =>
  isString(children) ? <td className={className}>{children}</td> : children;

const labelCellOverrides = {
  amountInformation: () => 'Amount',
  costBasis: () => 'Cost Basis',
  fromInformation: () => 'From',
  toInformation: () => 'To',
  tradeDate: () => 'Date',
  userCancellable: () => '',
  default: () => null
};

const valueCellOverrides = (cellValueClass, transactionType) => ({
  amountInformation: (amountInformation) => (
    <Amount {...{ amountInformation, cellValueClass }} />
  ),
  costBasis: (costBasis) => <CostBasis {...{ cellValueClass, costBasis }} />,
  fromInformation: (fromInformation, { bankAccountNumber }) => (
    <FromAccountInfo
      {...{ bankAccountNumber, cellValueClass, fromInformation }}
    />
  ),
  toInformation: (toInformation, { bankAccountNumber }) => (
    <ToAccountInfo {...{ bankAccountNumber, cellValueClass, toInformation }} />
  ),
  tradeDate: (tradeDate) => <TradeDate {...{ cellValueClass, tradeDate }} />,
  userCancellable: (userCancellable, account) => (
    <CancelTransaction
      transactionType={transactionType}
      confirmationNumber={account.typeInformation.confirmationNumber}
      fromProductType={account.fromInformation?.accountFromInfo?.productType}
      userCancellable={userCancellable}
      renderAsLink
    />
  ),
  default: () => null
});

const Amount = ({ amountInformation, cellValueClass }) => {
  const { amount, quantity, unitCode, productType } = amountInformation;

  return (
    <Optioner val={unitCode}>
      {{
        [DOLLAR_UNIT_TYPE_CODE]: (
          <Dollar element="td" className={cellValueClass}>
            {amount}
          </Dollar>
        ),
        [SHARES_UNIT_TYPE_CODE]: (
          <Shares
            element="td"
            extraDecimal={isBrokerage(productType) || isActivePlus(productType)}
            className={cellValueClass}>
            {quantity}
          </Shares>
        ),
        [PERCENT_UNIT_TYPE_CODE]: (
          <Percent element="td" className={cellValueClass}>
            {amount}
          </Percent>
        ),
        [AVAILABLE_SHARES_UNIT_TYPE_CODE]: (
          <td className={cellValueClass}>{AVAILABLE_SHARES_LABEL}</td>
        ),
        [REMAINING_SHARES_UNIT_TYPE_CODE]: (
          <td className={cellValueClass}>{REMAINING_SHARES_LABEL}</td>
        ),
        _: <DefaultCell className={cellValueClass} />
      }}
    </Optioner>
  );
};

const CostBasis = ({ costBasis, cellValueClass }) => (
  <td className={cellValueClass}>
    {costBasis ? <div>{costBasis}</div> : <div>-</div>}
  </td>
);

const BankAccountNumber = ({ bankAccountNumber }) => (
  <Fragment>{`Bank Account No. ${bankAccountNumber}`}</Fragment>
);

const AccountNumberOwners = ({ accountWChecksum, owners }) => (
  <Fragment>
    <div className="mobile-transaction-pending-table-account-number">
      {accountWChecksum}
    </div>
    <div className="mobile-transaction-pending-table-account-owners">
      {!isEmpty(owners) ? owners : ''}
    </div>
  </Fragment>
);

const AccountWChecksum = ({ toAccountWChecksum }) => (
  <div className="mobile-transaction-pending-tableaccount-number">
    {toAccountWChecksum}
  </div>
);

const AccountNumberOwnersWithPermissions =
  WithUserPermissions(AccountNumberOwners);
const BankAccountNumberWithPermissions = WithUserPermissions(BankAccountNumber);
const AccountWChecksumWithPermissions = WithUserPermissions(AccountWChecksum);

const FromAccount = ({
  accountName,
  accountType,
  accountWChecksum,
  owners,
  ticker
}) => (
  <Fragment>
    <div className="mobile-transaction-pending-table-account-type">
      {accountType} - {accountName}
    </div>
    <div className="mobile-transaction-pending-table-account-ticker">
      {ticker}
    </div>
    <AccountNumberOwnersWithPermissions
      accountWChecksum={accountWChecksum}
      owners={owners}
      shouldMask
    />
  </Fragment>
);

const FromAccountInfo = ({
  bankAccountNumber,
  cellValueClass,
  fromInformation
}) => {
  const { accountFromInfo, engagementName, type } = fromInformation || {};

  const isGiftTransactions =
    isPurchaseTransactionType(type) && engagementName === GO_TUITION;
  const isAlternateTransactions =
    isPurchaseTransactionType(type) ||
    ((isRedeemTransactionType(type) ||
      isExchangeTransactionType(type) ||
      isBaseTransactionType(type)) &&
      engagementName === GO_TUITION);
  const hasAccountInfo = !!accountFromInfo;

  return (
    <td className={cellValueClass}>
      <Optioner
        val={{ isGiftTransactions, isAlternateTransactions, hasAccountInfo }}>
        {{
          isGiftTransactions: 'GoTuition',
          isAlternateTransactions: () =>
            isEmpty(bankAccountNumber) ? (
              '-'
            ) : (
              <BankAccountNumberWithPermissions
                bankAccountNumber={bankAccountNumber}
                shouldMask
              />
            ),
          hasAccountInfo: () => <FromAccount {...accountFromInfo} />,
          _: '-'
        }}
      </Optioner>
    </td>
  );
};

const ToAccountInfo = ({
  bankAccountNumber,
  cellValueClass,
  toInformation
}) => {
  const {
    accountToInfo,
    deliveryType,
    toName,
    toTicker,
    toAccountWChecksum,
    type
  } = toInformation || {};

  const hasRedeemType = isRedeemTransactionType(type);
  const hasAccountToInfo = Boolean(accountToInfo);
  const hasToName = !isEmpty(toName);
  const hasDistribution = isDistributionToSchoolTransactionType(type);

  return (
    <Fragment>
      <Optioner
        val={{ hasRedeemType, hasAccountToInfo, hasToName, hasDistribution }}>
        {{
          hasRedeemType: (
            <Optioner val={deliveryType}>
              {{
                [WIRE_DELIVERY_TYPE]: (
                  <td className={cellValueClass}>
                    <div className="mobile-transaction-pending-table-masked-account-number">
                      Wire to your Bank of Record
                    </div>
                    <div>
                      {isEmpty(bankAccountNumber) ? (
                        '-'
                      ) : (
                        <BankAccountNumberWithPermissions
                          bankAccountNumber={bankAccountNumber}
                          shouldMask
                        />
                      )}
                    </div>
                  </td>
                ),
                [ELECTRONIC_DELIVERY_TYPE]: () => (
                  <td className={cellValueClass}>
                    <div className="mobile-transaction-pending-table-masked-account-number">
                      Electronic Transfer to your Bank of Record
                    </div>
                    <div>
                      {isEmpty(bankAccountNumber) ? (
                        '-'
                      ) : (
                        <BankAccountNumberWithPermissions
                          bankAccountNumber={bankAccountNumber}
                          shouldMask
                        />
                      )}
                    </div>
                  </td>
                ),
                [ADDRESS_OF_RECORD_DELIVERY_TYPE]: () => (
                  <td className={cellValueClass}>
                    <div className="mobile-transaction-pending-table-masked-account-number">
                      Check to your address of Record
                    </div>
                  </td>
                )
              }}
            </Optioner>
          ),
          hasDistribution: (
            <Optioner val={deliveryType}>
              {{
                [ADDRESS_OF_RECORD_DELIVERY_TYPE]: () => (
                  <td className={cellValueClass}>
                    <div className="mobile-transaction-pending-table-masked-account-number">
                      {toInformation.payeeDetail ? (
                        <SchoolAddress
                          name={toInformation.payeeDetail?.college?.name}
                          address={toInformation.payeeDetail?.payeeAddress}
                        />
                      ) : (
                        'Check to your address of Record'
                      )}
                    </div>
                  </td>
                )
              }}
            </Optioner>
          ),
          hasAccountToInfo: (
            <td className={cellValueClass}>
              <FromAccount {...accountToInfo} />
            </td>
          ),
          hasToName: (
            <td className={cellValueClass}>
              <div className="mobile-transaction-pending-table-account-type">
                {toName}
              </div>
              <div className="mobile-transaction-pending-table-account-ticker">
                {toTicker}
              </div>
              <AccountWChecksumWithPermissions
                toAccountWChecksum={toAccountWChecksum}
                shouldMask
              />
            </td>
          ),
          _: <DefaultCell className={cellValueClass} />
        }}
      </Optioner>
    </Fragment>
  );
};

const AccountDetailsHeaderRow = ({ toggle, on, details }) => (
  <tr
    className="mobile-transaction-pending-table-row type-label-header-row"
    onClick={toggle}>
    <TypeInformation
      cellValueClass={`mobile-transaction-pending-table-type-label ${
        on ? 'expanded' : ''
      }`}
      colSpan={`${on ? 2 : 1}`}
      typeInformation={details.typeInformation}
    />

    {!on && (
      <Amount
        amountInformation={details.amountInformation}
        cellValueClass="mobile-transaction-pending-table-amount-value"
      />
    )}
  </tr>
);

const TradeDate = ({ tradeDate, cellValueClass }) =>
  tradeDate && (
    <FormattedDate element="td" className={cellValueClass}>
      {tradeDate}
    </FormattedDate>
  );

const TypeInformation = ({ typeInformation, cellValueClass, ...props }) => {
  const { confirmationNumber, typeDescriptor } = typeInformation || {};

  return (
    <td className={cellValueClass} {...props}>
      <div className="mobile-transaction-pending-table-type-descriptor">
        {typeDescriptor === 'DistributionToSchool'
          ? 'Distribution'
          : typeDescriptor}
      </div>
      <div className="mobile-transaction-pending-table-type-confirmation-number">
        {confirmationNumber}
      </div>
    </td>
  );
};

const AccountRows = ({ account, productType, hasCollegeSavingsOnly }) => (
  <Toggle>
    {({ on, toggle }) => (
      <Fragment>
        <AccountDetailsHeaderRow toggle={toggle} on={on} details={account} />
        {on && (
          <DetailCollection
            details={account}
            productType={productType}
            hasCollegeSavingsOnly={hasCollegeSavingsOnly}
          />
        )}
      </Fragment>
    )}
  </Toggle>
);

const MobileTransactionPendingTable = ({
  groups,
  productType,
  viewMoreLink,
  includePendingTransactions,
  hasCollegeSavingsOnly
}) => {
  return (
    <div className="mobile-transaction-pending-table-container">
      {includePendingTransactions && (
        <div className="account-details-pending-transactions">
          <h2 className="account-details-graph-header flex-header">
            Pending Transactions
            <a className="view-more-link" onClick={viewMoreLink}>
              View Pending History
            </a>
          </h2>
        </div>
      )}
      {isEmpty(groups) ? (
        <div className="mobile-transaction-pending-table-no-transactions">
          You currently do not have any Pending Transactions.
        </div>
      ) : (
        <Fragment>
          <table className="mobile-transaction-pending-table">
            <thead className="mobile-transactions-header">
              <TableColumns columns={columns} />
            </thead>
            <tbody>
              {map(groups, (rows, date) => (
                <Fragment key={date}>
                  <tr className="mobile-transaction-pending-table-row">
                    <FormattedDate
                      element="td"
                      className="mobile-transaction-pending-table-trade-date"
                      colSpan={2}>
                      {date}
                    </FormattedDate>
                  </tr>
                  {rows.map((row, r) => (
                    <AccountRows
                      key={date + r}
                      account={row}
                      productType={productType}
                      hasCollegeSavingsOnly={hasCollegeSavingsOnly}
                    />
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          <p className="transactions-pending-disclosure">
          {isMoneyMarketCutoffEnabled() ? Content.cancelMoneyMarketCutOffDisclosure : Content.cancelPendingTransactionsDisclosure}
          </p>
        </Fragment>
      )}
    </div>
  );
};

MobileTransactionPendingTable.defaultProps = {
  includePendingTransactions: false,
  productType: MUTUAL_FUND,
  viewMoreLink: '',
  hasCollegeSavingsOnly: false
};

export default MobileTransactionPendingTable;
